import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Pencil, Play, Loader2, AlertTriangle, CheckCircle2, History } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AutomationTemplates } from "@/components/AutomationTemplates";
import { AutomationActivity } from "@/components/AutomationActivity";
import { AutomationEmailForm, EMPTY_EMAIL } from "@/components/AutomationEmailForm";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { SmartField, composeText, splitText, buildSuggestions } from "@/components/AutomationFields";

function buildExamples(v) {
  const ph = v.photographers?.[0];
  return [
    { when: "a Floor Plans product is due tomorrow", cond: "still new", then: "remind me" },
    { when: "a new order is booked", cond: "not Photos", then: "remind me" },
    { when: "the day before a shoot", cond: "", then: "email the photographer" },
    ph ? { when: "a shoot is booked", cond: `not photographer ${ph}`, then: "remind me" } : { when: "a shoot is rescheduled", cond: "", then: "remind me" },
    { when: "every weekday at 7am", cond: "unless it's Ready", then: "email me" },
    v.clients?.[0] ? { when: "a product is due tomorrow", cond: `except for ${v.clients[0]}`, then: "remind me" } : { when: "a product is due tomorrow", cond: "unless it's Ready", then: "remind me" },
  ];
}

const Plan = ({ p, testid }) => (
  <div className="space-y-1.5 text-sm" data-testid={testid}>
    {[["When", p.summary.when], ["If", p.summary.if], ["Then", p.summary.then]].map(([k, v]) => (
      <div key={k} className="flex gap-3">
        <span className="w-12 shrink-0 font-mono text-[11px] uppercase tracking-wider text-primary">{k}</span>
        <span className="text-foreground/90">{v}</span>
      </div>
    ))}
  </div>
);

const SetupSteps = ({ items }) => (
  <div className="mt-3 space-y-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="automation-setup-steps">
    {items.map((u) => (
      <div key={u.need}>
        <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> Needs setup: {u.need}</div>
        <ol className="mt-1 list-decimal space-y-0.5 pl-5 text-xs text-muted-foreground">{u.steps.map((s) => <li key={s}>{s}</li>)}</ol>
      </div>
    ))}
  </div>
);

function Editor({ initial, onSaved, onCancel }) {
  const [parts, setParts] = useState(() => ({ when: "", cond: "", then: "", ...(initial?.parts || splitText(initial?.text)) }));
  const [name, setName] = useState(initial?.name || "");
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [vocab, setVocab] = useState({});
  const [email, setEmail] = useState(() => ({ ...EMPTY_EMAIL, ...(initial?.email || {}) }));
  const wantsEmail = /\bsend (?:the |an? |my |custom |our )?(?:custom )?e-?mail\b/i.test(parts.then);
  const timer = useRef(null);
  const text = parts.when.trim() && parts.then.trim() ? composeText(parts) : "";
  const sugg = buildSuggestions(vocab);
  const examples = buildExamples(vocab);
  const setPart = (k) => (v) => setParts((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/automations/vocab").then(({ data }) => setVocab(data)).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (text.trim().length < 8) { setParsed(null); return undefined; }
    timer.current = setTimeout(() => {
      api.post("/automations/parse", { text }).then(({ data }) => setParsed(data)).catch(() => {});
    }, 350);
    return () => clearTimeout(timer.current);
  }, [text]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { text, name, parts, ...(wantsEmail ? { email } : {}) };
      const { data } = initial
        ? await api.put(`/automations/${initial.id}`, payload)
        : await api.post("/automations", payload);
      toast.success(data.status === "needs_setup" ? "Saved. It's off until the setup steps are done." : "Automation saved and turned on");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };

  return (
    <div className="flex flex-col gap-4" data-testid="automation-editor">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (optional)"
        className="h-9 w-full rounded-md border border-border bg-background px-3 text-sm" data-testid="automation-name-input" />
      <div className="grid gap-4">
        <SmartField label="When" hint="what starts it" value={parts.when} onChange={setPart("when")} options={sugg.when}
          placeholder="a shoot is booked" testid="automation-when-input" />
        <SmartField label="If" hint="optional · use “not” to leave out" value={parts.cond} onChange={setPart("cond")} options={sugg.cond}
          placeholder="photographer is … and not Photos" testid="automation-if-input" />
        <SmartField label="Then" hint="what to do" value={parts.then} onChange={setPart("then")} options={sugg.then}
          placeholder="remind me" testid="automation-then-input" />
        {!wantsEmail && (
          <button type="button" onClick={() => setPart("then")(parts.then.trim() ? `${parts.then.trim()} and send the email` : "send the email")}
            className="self-start text-xs text-primary hover:underline" data-testid="automation-add-email-btn">+ Send an email</button>
        )}
      </div>
      {wantsEmail && <AutomationEmailForm value={email} onChange={setEmail} team={vocab.team_members || []} />}
      {text && <p className="text-xs italic text-muted-foreground" data-testid="automation-composed-text">“{text}”</p>}
      {!initial && (
        <div className="flex flex-wrap gap-1.5">
          <span className="py-1 text-[11px] text-muted-foreground">Examples:</span>
          {examples.map((ex, i) => (
            <button key={i} onClick={() => setParts(ex)} data-testid={`automation-example-${i}`}
              className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground">{composeText(ex)}</button>
          ))}
        </div>
      )}
      {parsed && (
        <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid="automation-preview">
          <div className="mb-2 text-xs font-medium text-muted-foreground">How the app reads it</div>
          <Plan p={parsed} testid="automation-preview-plan" />
          {parsed.unsupported?.length > 0 && <SetupSteps items={parsed.unsupported} />}
          {parsed.errors?.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs text-rose-400" data-testid="automation-preview-errors">
              {parsed.errors.map((e) => <li key={e}>• {e}</li>)}
            </ul>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="outline" onClick={onCancel} data-testid="automation-cancel-btn">Cancel</Button>
        <Button onClick={save} disabled={saving || !parsed || parsed.errors?.length > 0} data-testid="automation-save-btn">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save automation
        </Button>
      </div>
    </div>
  );
}

function Runs({ id }) {
  const [runs, setRuns] = useState(null);
  useEffect(() => { api.get(`/automations/${id}/runs`).then(({ data }) => setRuns(data.runs)).catch(() => setRuns([])); }, [id]);
  if (!runs) return <Loader2 className="mt-3 h-4 w-4 animate-spin text-muted-foreground" />;
  if (!runs.length) return <p className="mt-3 text-xs text-muted-foreground">No runs yet.</p>;
  return (
    <ul className="mt-3 space-y-1.5 text-xs" data-testid={`automation-runs-${id}`}>
      {runs.map((r) => (
        <li key={r.at} className="text-muted-foreground">
          <span className="text-foreground">{new Date(r.at).toLocaleString()}</span> · {r.matched} matched{r.manual ? " (run now)" : ""}
          {r.items?.length > 0 && <span> — {r.items.slice(0, 3).map((x) => `${x.type} @ ${x.address}`).join("; ")}{r.items.length > 3 ? "…" : ""}</span>}
        </li>
      ))}
    </ul>
  );
}

function Card({ a, onChange, onEdit }) {
  const [busy, setBusy] = useState(false);
  const [showRuns, setShowRuns] = useState(false);
  const needs = a.status === "needs_setup";
  const act = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); if (msg) toast.success(typeof msg === "function" ? msg(r) : msg); onChange(); } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <div className={`relative overflow-hidden rounded-xl border bg-card p-5 shadow-lg shadow-black/25 ring-1 ring-white/5 transition-transform hover:-translate-y-0.5 ${needs ? "border-amber-500/50" : a.enabled ? "border-primary/30" : "border-border"}`} data-testid={`automation-card-${a.id}`}>
      <span className={`absolute inset-y-0 left-0 w-1 ${needs ? "bg-amber-400" : a.enabled ? "bg-primary" : "bg-muted-foreground/30"}`} />
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{a.name || "Automation"}</span>
            <span data-testid={`automation-status-${a.id}`}
              className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${needs ? "bg-amber-500/15 text-amber-300" : a.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
              {needs ? "Needs setup" : a.enabled ? "Active" : "Paused"}
            </span>
          </div>
          <p className="mt-1 text-sm italic text-muted-foreground">“{a.text}”</p>
        </div>
        <Switch checked={a.enabled} disabled={busy || needs} data-testid={`automation-toggle-${a.id}`}
          onCheckedChange={(v) => act(() => api.put(`/automations/${a.id}`, { enabled: v }), v ? "Turned on" : "Paused")} />
      </div>
      <div className="mt-4"><Plan p={a.parsed} testid={`automation-plan-${a.id}`} /></div>
      {needs && <SetupSteps items={a.parsed.unsupported} />}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
        <span className="mr-auto">{a.last_run_at ? `Last run ${new Date(a.last_run_at).toLocaleString()} · ${a.last_matched ?? 0} matched` : "Not run yet"}</span>
        <button onClick={() => setShowRuns((s) => !s)} className="flex items-center gap-1 hover:text-foreground" data-testid={`automation-history-${a.id}`}><History className="h-3.5 w-3.5" /> History</button>
        {!needs && (
          <button onClick={() => act(async () => (await api.post(`/automations/${a.id}/run`)).data, (r) => `Ran now: ${r.matched} matched`)} disabled={busy}
            className="flex items-center gap-1 hover:text-foreground" data-testid={`automation-run-${a.id}`}><Play className="h-3.5 w-3.5" /> Run now</button>
        )}
        <button onClick={() => onEdit(a)} className="flex items-center gap-1 hover:text-foreground" data-testid={`automation-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button>
        <button onClick={() => window.confirm("Delete this automation?") && act(() => api.delete(`/automations/${a.id}`), "Deleted")}
          className="flex items-center gap-1 text-rose-400 hover:text-rose-300" data-testid={`automation-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
      </div>
      {showRuns && <Runs id={a.id} />}
    </div>
  );
}

export default function Automations() {
  const [items, setItems] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [editing, setEditing] = useState(null);
  const [tick, setTick] = useState(0);
  const load = () => setTick((t) => t + 1) || api.get("/automations").then(({ data }) => { setItems(data.automations); setLastCheck(data.last_check_at); }).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, []);
  const done = () => { setEditing(null); load(); };

  return (
    <div>
      <PageHeader title="Automations" subtitle="Write rules in plain English. The app reads them and does the work." testid="automations-header">
        <Button onClick={() => setEditing("new")} className="gap-2" data-testid="automation-new-btn"><Plus className="h-4 w-4" /> New automation</Button>
      </PageHeader>
      <div className="space-y-4 p-6 md:p-8">
        <Sheet open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl" data-testid="automation-drawer">
            <SheetHeader className="mb-5 text-left">
              <SheetTitle>{editing === "new" ? "New automation" : "Edit automation"}</SheetTitle>
              <SheetDescription>Fill in When, If and Then. Suggestions appear as you type.</SheetDescription>
            </SheetHeader>
            {editing !== null && <Editor key={editing === "new" ? "new" : editing.id} initial={editing === "new" ? null : editing} onSaved={done} onCancel={() => setEditing(null)} />}
          </SheetContent>
        </Sheet>
        <AutomationActivity refreshKey={tick} />
        {items !== null && <AutomationTemplates existing={items} onAdded={load} />}
        <div className="flex items-center gap-2 pt-2">
          <h2 className="text-sm font-semibold">Your automations</h2>
          <span className="ml-auto text-[11px] text-muted-foreground" data-testid="automation-last-check">
            Checks every 15 min{lastCheck ? ` · last automatic check ${new Date(lastCheck).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : ""} · pulls new orders &amp; shoots from Aryeo each time
          </span>
        </div>
        {items === null ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground" data-testid="automations-empty">
            <CheckCircle2 className="mx-auto mb-2 h-6 w-6 text-primary" />No automations yet. Click “New automation” and describe what should happen.
          </div>
        ) : items.map((a) => <Card key={a.id} a={a} onChange={load} onEdit={setEditing} />)}
      </div>
    </div>
  );
}
